import {
  mapUserMeType,
  PermissionNameUnionType,
  PersonJobType,
  useFindOwnUserSuspense,
} from '@api';
import {
  TouringCard,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
  TouringDataTable,
} from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Button, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useCheckedJobs, useColumns } from './hooks';
import { useConfirmJobs } from './hooks/useConfirmJobs';

const TRANS_PATH = 'TOUR_REP_SELF_PROMOTE.UNCONFIRMED_SHOWS';

type UnconfirmedShowsPropsType = {
  unconfirmedShows: PersonJobType[];
  personId: string;
  isPending: boolean;
  assignedUserId: string | null;
};

export const UnconfirmedShows: React.FC<UnconfirmedShowsPropsType> = ({
  unconfirmedShows,
  personId,
  isPending,
  assignedUserId,
}) => {
  const { t } = useTranslation('personDetailsTourRep');
  const { checkedJobIds, areAllJobIdsChecked, toggleAllJobIds, onCheckJobId } =
    useCheckedJobs(unconfirmedShows);
  const { data: userData } = useFindOwnUserSuspense({
    query: { select: (data) => mapUserMeType(data.data) },
  });
  const isEditingOwnUser = userData.id === assignedUserId;
  const { columns } = useColumns({
    checkedJobIds,
    onCheckJobId,
    isEditingOwnUser,
  });
  const { confirmJobs } = useConfirmJobs(personId);
  const { hasOneOfPermissions } =
    usePermissionContext<PermissionNameUnionType>();

  const hasPermissionToConfirm = hasOneOfPermissions([
    {
      permission: 'ADDRESSBOOK_TOURREP_JOBREQUEST_CONFIRMATION',
      action: 'READ',
    },
    {
      permission: 'ADDRESSBOOK_SELFPROMOTE_JOBREQUEST_CONFIRMATION',
      action: 'READ',
    },
  ]);

  const toggleButtonTitle = areAllJobIdsChecked
    ? t(`${TRANS_PATH}.SELECT_NONE_BUTTON`)
    : t(`${TRANS_PATH}.SELECT_ALL_BUTTON`);

  const isAllowedToConfirm = hasPermissionToConfirm || isEditingOwnUser;

  return (
    <>
      <TouringCard data-testid="unconfirmed-shows-container">
        <TouringCardHeader>
          <TouringCardTitle>{t(`${TRANS_PATH}.TITLE`)}</TouringCardTitle>
        </TouringCardHeader>
        <TouringCardSection>
          <TouringDataTable<PersonJobType>
            records={unconfirmedShows}
            columns={columns}
            idAccessor="showTypeMinified.showId"
            fetching={isPending}
            withColumnBorders={false}
            withTableBorder={false}
          />
        </TouringCardSection>
      </TouringCard>
      {isAllowedToConfirm && (
        <Group justify="flex-end">
          <Button onClick={toggleAllJobIds} variant="outline">
            {toggleButtonTitle}
          </Button>
          <Button onClick={() => confirmJobs(checkedJobIds)}>
            {t(`${TRANS_PATH}.CONFIRM_BUTTON`)}
          </Button>
        </Group>
      )}
    </>
  );
};
